import { Browser } from "../../core/browser";
import { AuxiliumComponent } from "../auxilium-component";

export default class ColourPicker extends AuxiliumComponent {
	static observable = ["value", "size", "label", "hidden-name", "helper-text"];

	#hue = 0;
	#saturation = 1;
	#lightness = .5;

	#availablePresets = Browser.GetThemeColours();

	#terminateValueSetter = false;
	#suppressInputMode = "";

	#ControlIndex = {
		SpectrumCursor: null,
		SpectrumCanvas: null,
		SpectrumDimensions: null,
		HueCursor: null,
		HueCanvas: null,
		HueDimensions: null,

		Inputs: {
			R: null,
			G: null,
			B: null,

			H: null,
			S: null,
			V: null,

			C: null,
			M: null,
			Y: null,
			K: null,

			Hex: null
		}
	};

	constructor() {
		super();

		let defaultColour = this.closest("[data-theme]")?.dataset.theme === "dark" ? "rgb(255,255,255)" : "rgb(0,0,0)";
		if (this.hasAttribute("presets")) { defaultColour = ""; }

		this.onchange = null;
		this.value = this.getAttribute("value") ?? defaultColour;
		this.presets = this.hasAttribute("presets") ? (this.getAttribute("presets") ?? "").toLowerCase().split(",").map(x => x.trim()).filter(x => this.#availablePresets.includes(x) || x === "all") : "";

		if (this.hasAttribute("include-corporate")) { this.#availablePresets.push("chipside", "oppidatim", "mipermit"); }
		if (this.hasAttribute("include-none")) { this.#availablePresets.unshift(""); }

		if (this.presets.includes("all")) {
			this.presets = this.#availablePresets;
		}

		if (this.closest("form") && !this.hiddenName) {
			this.hiddenName = this.id ?? "";
		}
	}

	parsedCallback() {
		if (!this.presets.length) {
			this.#BuildManualPicker();
		} else {
			this.#BuildPresetPicker();
		}
	}

	#BuildPresetPicker() {
		if (!this.#availablePresets.includes(this.value?.toLowerCase())) {
			this.value = "";
		}

		this.innerHTML = `<chip-dropdown button-id="${this.identifier}"></chip-dropdown>`;

		const dropdown = this.querySelector("chip-dropdown"),
			{ label, helperText, hiddenName } = this;

		Object.assign(dropdown, {
			label, helperText, hiddenName,
			value: this.value,
			icon: this.value ? "fas fa-square" : "fal fa-minus-square",
			text: this.value ? toTitleCase(this.value) : "None"
		});

		dropdown.dataset.colour = this.value.toLowerCase();

		if (this.size) { dropdown.size = this.size; }

		dropdown.onchange = () => {
			dropdown.text = toTitleCase(dropdown.value) || "None";
			dropdown.dataset.colour = dropdown.value.toLowerCase();

			dropdown.querySelector(".input-field>button>chip-icon").icon = dropdown.value !== "" ? "fas fa-square" : "fal fa-minus-square";

			this.value = dropdown.value;

			this.raiseEvent("change");
		}

		this.presets.forEach(colour => {
			const preset = document.createElementWithContents("chip-dropdownitem",
				`
					<div class="colour-picker__preset"></div>
				`.trim(), { value: colour.toLowerCase(), tooltip: toTitleCase(colour) || "None" }
			);

			preset.firstElementChild.dataset.colour = colour.toLowerCase();

			if (colour.trim() === "") {
				preset.firstElementChild.appendChild(document.createElementWithContents("chip-icon", null, { icon: "fas fa-ban" }));
			}

			dropdown.appendChild(preset);
		});
	}

	#BuildManualPicker() {
		this.innerHTML =
			`
				<chip-dropdown button-id="${this.identifier}" icon='fas fa-square'>
					<chip-dropdownitem detached>
						<div class="colour-picker--canvas-wrapper">
							<div class="colour-picker__canvas-container spectrum-container">
								<div class="spectrum-cursor"></div>
								<canvas class="spectrum-canvas"></canvas>
							</div>
							<div class="colour-picker__canvas-container hue-container">
								<div class="hue-cursor"></div>
								<canvas class="hue-canvas"></canvas>
							</div>

							<div class="colour-picker__values-wrapper">
								<div class="colour-picker__value-section">
									<chip-input class="colour-picker__rgb-r" orientation="horizontal" type="number" value="0" min="0" size="sm" max="255" label="R"></chip-input>
									<chip-input class="colour-picker__rgb-g" orientation="horizontal" type="number" value="0" min="0" max="255" size="sm" label="G"></chip-input>
									<chip-input class="colour-picker__rgb-b" orientation="horizontal" type="number" value="0" min="0" max="255" size="sm" label="B"></chip-input>
								</div>

								<div class="colour-picker__value-section">
									<chip-input orientation="horizontal" class="colour-picker__hsv-h" type="number" value="0" min="0" size="sm" max="360" label="H"></chip-input>
									<chip-input orientation="horizontal" class="colour-picker__hsv-s" type="number" value="0" min="0" max="100" size="sm" label="S"></chip-input>
									<chip-input orientation="horizontal" class="colour-picker__hsv-v" type="number" value="0" max="100" min="0" size="sm" label="V"></chip-input>
								</div>

								<div class="colour-picker__value-section">
									<chip-input orientation="horizontal" class="colour-picker__cmyk-c" type="number" value="0" min="0" max="100" size="sm" label="C"></chip-input>
									<chip-input orientation="horizontal" class="colour-picker__cmyk-m" type="number" value="0" max="100" min="0" size="sm" label="M"></chip-input>
									<chip-input orientation="horizontal" class="colour-picker__cmyk-y" type="number" value="0" max="100" min="0" size="sm" label="Y"></chip-input>
									<chip-input orientation="horizontal" class="colour-picker__cmyk-k" type="number" value="100" max="100" min="0" size="sm" label="K"></chip-input>
								</div>

								<div class="colour-picker__hex-wrapper">
									<chip-input
										label="Hex"
										value="000000"
										casing="uppercase"
										prefix-icon="fas fa-hashtag"
										size="sm"
										icon-style="integrated"
										maxlength="6">
									</chip-input>
								</div>
							</div>
						</div>
					</chip-dropdownitem>
				</chip-dropdown>
			`.trim();

		const dropdown = this.querySelector("chip-dropdown"),
			{ label, helperText, hiddenName } = this;

		Object.assign(dropdown, {
			text: this.#parseValue(),
			label, helperText, hiddenName
		});

		if (this.size) { dropdown.size = this.size; }

		Object.assign(this.#ControlIndex, {
			SpectrumCursor: dropdown.querySelector(".spectrum-cursor"),
			SpectrumCanvas: dropdown.querySelector(".spectrum-canvas"),
			HueCursor: dropdown.querySelector(".hue-cursor"),
			HueCanvas: dropdown.querySelector(".hue-canvas"),

			Inputs: {
				R: dropdown.querySelector(".colour-picker__rgb-r"),
				G: dropdown.querySelector(".colour-picker__rgb-g"),
				B: dropdown.querySelector(".colour-picker__rgb-b"),

				H: dropdown.querySelector(".colour-picker__hsv-h"),
				S: dropdown.querySelector(".colour-picker__hsv-s"),
				V: dropdown.querySelector(".colour-picker__hsv-v"),

				C: dropdown.querySelector(".colour-picker__cmyk-c"),
				M: dropdown.querySelector(".colour-picker__cmyk-m"),
				Y: dropdown.querySelector(".colour-picker__cmyk-y"),
				K: dropdown.querySelector(".colour-picker__cmyk-k"),

				Hex: dropdown.querySelector(".colour-picker__hex-wrapper chip-input")
			}
		});

		this.#createHueSpectrum();
		dropdown.querySelector(".spectrum-canvas").onmousedown = (event) => this.#OnSpectrumMousedown(event);

		[this.#ControlIndex.Inputs.R, this.#ControlIndex.Inputs.G, this.#ControlIndex.Inputs.B].forEach(x => x.oninput = () => {
			this.#suppressInputMode = "rgb";

			this.#colourToPos(`rgb(${this.#ControlIndex.Inputs.R.value},${this.#ControlIndex.Inputs.G.value},${this.#ControlIndex.Inputs.B.value})`);
		});

		[this.#ControlIndex.Inputs.H, this.#ControlIndex.Inputs.S, this.#ControlIndex.Inputs.V].forEach(x => x.oninput = () => {
			this.#suppressInputMode = "hsv";

			this.#colourToPos(`hsv ${this.#ControlIndex.Inputs.H.value} ${(parseFloat(this.#ControlIndex.Inputs.S.value) || 0) / 100} ${(parseFloat(this.#ControlIndex.Inputs.V.value) || 0) / 100}`);
		});

		[this.#ControlIndex.Inputs.C, this.#ControlIndex.Inputs.M, this.#ControlIndex.Inputs.Y, this.#ControlIndex.Inputs.K].forEach(x => x.oninput = () => {
			this.#suppressInputMode = "cmyk";

			this.#colourToPos(`cmyk ${this.#ControlIndex.Inputs.C.value} ${this.#ControlIndex.Inputs.M.value} ${this.#ControlIndex.Inputs.Y.value} ${this.#ControlIndex.Inputs.K.value}`);
		});

		this.#ControlIndex.Inputs.Hex.oninput = () => {
			this.#suppressInputMode = "hex";

			this.#colourToPos(`#${this.#ControlIndex.Inputs.Hex.value}`);
		}

		if (this.value) {
			const rgb = this.#valueToRGB(this.value);

			this.#hue = this.#rgbToHSL(rgb.r, rgb.g, rgb.b).h;
		}

		this.#UpdateValue(this.value);
		this.#colourToPos(this.value);
		this.#updateSpectrumCanvas(this.value);
	}

	#refreshDimensions() {
		this.#ControlIndex.SpectrumDimensions = this.#ControlIndex.SpectrumCanvas?.getBoundingClientRect();
		this.#ControlIndex.HueDimensions = this.#ControlIndex.HueCanvas?.getBoundingClientRect();
	}

	#createHueSpectrum() {
		const canvas = this.querySelector(".hue-canvas"),
			context = canvas.getContext("2d"),
			gradient = context.createLinearGradient(0, 0, 0, canvas.height);

		gradient.addColorStop(0.00, "hsl(0,100%,50%)");
		gradient.addColorStop(0.17, "hsl(298.8, 100%, 50%)");
		gradient.addColorStop(0.33, "hsl(241.2, 100%, 50%)");
		gradient.addColorStop(0.5, "hsl(180, 100%, 50%)");
		gradient.addColorStop(0.67, "hsl(118.8, 100%, 50%)");
		gradient.addColorStop(0.83, "hsl(61.2,100%,50%)");
		gradient.addColorStop(1, "hsl(360,100%,50%)");

		context.fillStyle = gradient;
		context.fillRect(0, 0, canvas.width, canvas.height);

		canvas.onmousedown = (event) => this.#OnHueMousedown(event);
	}

	#OnHueMousedown(event) {
		const self = this,
			initialValue = this.value;

		this.#suppressInputMode = "";

		this.#refreshDimensions();

		function onmousemove(ev) {
			ev.preventDefault();

			self.#MoveHueCursor(ev.pageY - self.#ControlIndex.HueDimensions.top - (document.documentElement.scrollTop || document.body.scrollTop));
			self.#UpdateValue(`hsl ${self.#hue} ${self.#saturation} ${self.#lightness}`);
		}

		function onmouseup() {
			window.removeEventListener("mousemove", onmousemove);
			window.removeEventListener("mouseup", onmouseup);

			if (self.value !== initialValue) { self.raiseEvent("change"); }
		}

		window.addEventListener("mousemove", onmousemove);
		window.addEventListener("mouseup", onmouseup);

		self.#MoveHueCursor(event.pageY - self.#ControlIndex.HueDimensions.top - (document.documentElement.scrollTop || document.body.scrollTop));
		self.#UpdateValue(`hsl ${self.#hue} ${self.#saturation} ${self.#lightness}`);
	}

	#OnSpectrumMousedown(event) {
		const self = this,
			initialValue = this.value;

		this.#suppressInputMode = "";

		this.#refreshDimensions();

		function onmousemove(ev) {
			ev.preventDefault();

			self.#MoveSpectrumCursor(ev.pageX - self.#ControlIndex.SpectrumDimensions.left, ev.pageY - self.#ControlIndex.SpectrumDimensions.top - (document.documentElement.scrollTop || document.body.scrollTop));
		}

		function onmouseup() {
			window.removeEventListener("mousemove", onmousemove);
			window.removeEventListener("mouseup", onmouseup);

			if (self.value !== initialValue) { self.raiseEvent("change"); }
		}

		window.addEventListener("mousemove", onmousemove);
		window.addEventListener("mouseup", onmouseup);

		self.#MoveSpectrumCursor(event.pageX - self.#ControlIndex.SpectrumDimensions.left, event.pageY - self.#ControlIndex.SpectrumDimensions.top - (document.documentElement.scrollTop || document.body.scrollTop));
	}

	#MoveHueCursor(yPos) {
		const y = Math.max(0, Math.min(this.#ControlIndex.HueDimensions.height, yPos)),
			percent = y / this.#ControlIndex.HueDimensions.height;

		this.#hue = 360 - (360 * percent);

		this.#updateSpectrumCanvas(`hsl ${this.#hue} 1 0.5`);

		Object.assign(this.#ControlIndex.HueCursor.style, {
			top: `${y}px`,
			backgroundColor: `hsl(${this.#hue}, 100%, 50%)`
		});

		this.#ControlIndex.SpectrumCursor.style.backgroundColor = `hsl(${this.#hue}, ${this.#saturation * 100}%, ${this.#lightness * 100}%)`;
		this.#UpdateValue(`hsl ${this.#hue} ${this.#saturation} ${this.#lightness}`);

		this.#terminateValueSetter = true;

		const rgb = this.#valueToRGB(`hsl ${this.#hue} ${this.#saturation} ${this.#lightness}`);
		this.value = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
	}

	#MoveSpectrumCursor(xPos, yPos) {
		let x = Math.min(Math.max(0, xPos - (document.documentElement.scrollLeft || document.body.scrollLeft)), this.#ControlIndex.SpectrumDimensions.width),
			y = Math.min(yPos, this.#ControlIndex.SpectrumDimensions.height);

		if (y < 0) { y = 0.1; }

		const xRatio = x / this.#ControlIndex.SpectrumDimensions.width * 100,
			yRatio = y / this.#ControlIndex.SpectrumDimensions.height * 100;

		const hsvValue = 1 - (yRatio / 100),
			hsvSaturation = xRatio / 100;

		this.#lightness = (hsvValue / 2) * (2 - hsvSaturation) || 0;
		this.#saturation = (hsvValue * hsvSaturation) / (1 - Math.abs(2 * this.#lightness - 1)) || 0;

		this.#UpdateValue(`hsl ${this.#hue} ${this.#saturation} ${this.#lightness}`);

		Object.assign(this.#ControlIndex.SpectrumCursor.style, {
			left: `${x}px`,
			top: `${y}px`,
			backgroundColor: `hsl(${this.#hue}, ${this.#saturation * 100}%, ${this.#lightness * 100}%)`
		});

		this.#terminateValueSetter = true;

		const rgb = this.#valueToRGB(`hsl ${this.#hue} ${this.#saturation} ${this.#lightness}`);
		this.value = `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
	}

	#UpdateValue(value) {
		const rgb = this.#valueToRGB(value),
			icon = this.querySelector("chip-dropdown .input-field>button>chip-icon");

		if (this.value) {
			Object.assign(this.querySelector("chip-dropdown"), {
				iconColour: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
				text: `${rgb.r}, ${rgb.g}, ${rgb.b}`,
				value: `${rgb.r}, ${rgb.g}, ${rgb.b}`
			});

			if (icon) {
				icon.icon = "fas fa-square";
			}

			if (this.#suppressInputMode !== "rgb") {
				this.#ControlIndex.Inputs.R.value = rgb.r;
				this.#ControlIndex.Inputs.G.value = rgb.g;
				this.#ControlIndex.Inputs.B.value = rgb.b;
			}

			if (this.#suppressInputMode !== "hsv") {
				const hsv = this.#rgbToHSV(rgb.r, rgb.g, rgb.b);

				this.#ControlIndex.Inputs.H.value = Math.round(hsv.h);
				this.#ControlIndex.Inputs.S.value = Math.round(hsv.s * 100);
				this.#ControlIndex.Inputs.V.value = Math.round(hsv.v * 100);
			}

			if (this.#suppressInputMode !== "hex") { this.#ControlIndex.Inputs.Hex.value = this.#rgbToHex(rgb.r, rgb.g, rgb.b); }

			if (this.#suppressInputMode !== "cmyk") {
				const cmyk = this.#rgbToCMYK(rgb.r, rgb.g, rgb.b);

				this.#ControlIndex.Inputs.C.value = Math.round(cmyk.c);
				this.#ControlIndex.Inputs.M.value = Math.round(cmyk.m);
				this.#ControlIndex.Inputs.Y.value = Math.round(cmyk.y);
				this.#ControlIndex.Inputs.K.value = Math.round(cmyk.k);
			}
		}
	}

	#hsvToHSL(h, s, v, l = v - v * s / 2, m = Math.min(l, 1 - l)) {
		return {
			h,
			s: m ? (v - l) / m : 0,
			l
		};
	}

	#cmykToHSV(c, m, y, k) {
		const rgb = this.#cmykToRGB(c, m, y, k);

		return this.#rgbToHSV(rgb.r, rgb.g, rgb.b);
	}

	#cmykToHSL(c, m, y, k) {
		return this.#rgbToHSL(this.#cmykToRGB(c, m, y, k));
	}

	#cmykToRGB(c, m, y, k) {
		c = (c / 100);
		m = (m / 100);
		y = (y / 100);
		k = (k / 100);

		c = c * (1 - k) + k;
		m = m * (1 - k) + k;
		y = y * (1 - k) + k;

		const r = Math.round((1 - c) * 255);
		const g = Math.round((1 - m) * 255);
		const b = Math.round((1 - y) * 255);

		return { r, g, b };
	}

	#hexToHSV(hex) {
		const rgb = this.#hexToRGB(hex);

		return this.#rgbToHSV(rgb.r, rgb.g, rgb.b);
	}

	#valueToHSV(value) {
		if (value.startsWith("hsv")) { return { h: parseFloat(value.split(" ").at(1)), s: parseFloat(value.split(" ").at(2)), v: parseFloat(value.split(" ").at(3)) } }
		if (value.startsWith("hsl")) { return this.#hslToHSV(parseFloat(value.split(" ").at(1)), parseFloat(value.split(" ").at(2)), parseFloat(value.split(" ").at(3))); }
		if (value.startsWith("#")) { return this.#hexToHSV(value); }
		if (value.startsWith("cmyk")) { return this.#cmykToHSV(parseFloat(value.split(" ").at(1)), parseFloat(value.split(" ").at(2)), parseFloat(value.split(" ").at(3)), parseFloat(value.split(" ").at(4))); }

		const fixed = value.replace("rgb(", "").replace(")", "").replace(/ /g, "").split(",").map(Number);

		return this.#rgbToHSV(fixed.at(0), fixed.at(1), fixed.at(2));
	}

	#valueToHSL(value) {
		if (value.startsWith("hsl")) { return { h: parseFloat(value.split(" ").at(1)), s: parseFloat(value.split(" ").at(2)), l: parseFloat(value.split(" ").at(3)) } }
		if (value.startsWith("hsv")) { return this.#hsvToHSL(parseFloat(value.split(" ").at(1)), parseFloat(value.split(" ").at(2)), parseFloat(value.split(" ").at(3))); }
		if (value.startsWith("#")) { return this.#rgbToHSL(this.#hexToRGB(value)); }
		if (value.startsWith("cmyk")) { return this.#cmykToHSL({ c: parseFloat(value.split(" ").at(1)), m: parseFloat(value.split(" ").at(2)), y: parseFloat(value.split(" ").at(3)), k: parseFloat(value.split(" ").at(3)) }); }

		const fixed = value.replace("rgb(", "").replace(")", "").replace(/ /g, "").split(",").map(Number);

		return this.#rgbToHSL(fixed.at(0), fixed.at(1), fixed.at(2));
	}

	#updateSpectrumCanvas(value) {
		const hsl = this.#valueToHSL(value);

		if (value) {
			const canvas = this.querySelector(".spectrum-canvas"),
				context = canvas.getContext("2d");

			context.clearRect(0, 0, canvas.width, canvas.height);
			context.fillStyle = `hsl(${hsl.h}, ${hsl.s * 100}%, ${hsl.l * 100}%)`;
			context.fillRect(0, 0, canvas.width, canvas.height);

			const whiteGradient = context.createLinearGradient(0, 0, canvas.width, 0);

			whiteGradient.addColorStop(0, "#ffffff");
			whiteGradient.addColorStop(1, "rgba(0, 0, 0, 0)");

			context.fillStyle = whiteGradient;
			context.fillRect(0, 0, canvas.width, canvas.height);

			const blackGradient = context.createLinearGradient(0, 0, 0, canvas.height);

			blackGradient.addColorStop(0, "rgba(0, 0, 0, 0)");
			blackGradient.addColorStop(1, "#000000");

			context.fillStyle = blackGradient;
			context.fillRect(0, 0, canvas.width, canvas.height);
		}
	}

	#colourToPos(colour) {
		this.#refreshDimensions();

		const hsv = this.#valueToHSV(colour);
		this.#hue = hsv.h;

		const x = this.#ControlIndex.SpectrumDimensions.width * hsv.s,
			y = this.#ControlIndex.SpectrumDimensions.height * (1 - hsv.v);

		this.#MoveHueCursor(this.#ControlIndex.HueDimensions.height - ((this.#hue / 360) * this.#ControlIndex.HueDimensions.height));
		this.#MoveSpectrumCursor(x, y);
	}

	#parseValue() {
		if (!this.value) { return "None"; }

		return this.value.toUpperCase().split(",").join(", ");
	}

	#hexToRGB(hex) {
		hex = hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (_, r, g, b) => r + r + g + g + b + b);

		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

		return result ? {
			r: parseInt(result[1], 16),
			g: parseInt(result[2], 16),
			b: parseInt(result[3], 16)
		} : { r: 0, g: 0, b: 0 };
	}

	#hslToHSV(h, s, l, v = s * Math.min(l, 1 - l) + l) {
		return {
			h,
			s: v ? 2 - 2 * l / v : 0,
			v
		};
	}

	#rgbToHSL(r, g, b) {
		r = Math.min(255, Math.max(0, r)) / 255;
		g = Math.min(255, Math.max(0, g)) / 255;
		b = Math.min(255, Math.max(0, b)) / 255;

		const vmax = Math.max(r, g, b), vmin = Math.min(r, g, b);
		let h, s, l = (vmax + vmin) / 2;

		if (vmax === vmin) {
			return { h: 0, s: 0, l };
		}

		const d = vmax - vmin;
		s = l > 0.5 ? d / (2 - vmax - vmin) : d / (vmax + vmin);

		if (vmax === r) h = (g - b) / d + (g < b ? 6 : 0);
		if (vmax === g) h = (b - r) / d + 2;
		if (vmax === b) h = (r - g) / d + 4;

		h /= 6;
		h *= 360;

		return { h, s, l };
	}

	#rgbToHSV(r, g, b) {
		r = Math.min(255, Math.max(0, r)) / 255;
		g = Math.min(255, Math.max(0, g)) / 255;
		b = Math.min(255, Math.max(0, b)) / 255;

		const max = Math.max(r, g, b),
			min = Math.min(r, g, b);

		let h, s, v = max;
		const d = max - min;

		s = max == 0 ? 0 : d / max;

		if (max == min) {
			h = 0;
		} else {
			switch (max) {
				case r:
					h = (g - b) / d + (g < b ? 6 : 0);
					break;
				case g:
					h = (b - r) / d + 2;
					break;
				case b:
					h = (r - g) / d + 4;
					break;
			}

			h /= 6;
		}

		h *= 360;

		return {
			h, s, v
		};
	}

	#rgbToHex(r, g, b) {
		r = Math.min(255, Math.max(0, r));
		g = Math.min(255, Math.max(0, g));
		b = Math.min(255, Math.max(0, b));

		return [r, g, b].map(x => {
			const hex = x.toString(16)
			return hex.length === 1 ? '0' + hex : hex
		}).join('').toUpperCase();
	}

	#rgbToCMYK(r, g, b) {
		let computedC = 0;
		let computedM = 0;
		let computedY = 0;
		let computedK = 0;

		r = Math.min(255, Math.max(0, r));
		g = Math.min(255, Math.max(0, g));
		b = Math.min(255, Math.max(0, b));

		// Black
		if (r == 0 && g == 0 && b == 0) {
			return {
				c: 0,
				m: 0,
				y: 0,
				k: 100
			};
		}

		computedC = 1 - (r / 255);
		computedM = 1 - (g / 255);
		computedY = 1 - (b / 255);

		const minCMY = Math.min(computedC,
			Math.min(computedM, computedY));

		computedC = (computedC - minCMY) / (1 - minCMY);
		computedM = (computedM - minCMY) / (1 - minCMY);
		computedY = (computedY - minCMY) / (1 - minCMY);
		computedK = minCMY;

		return {
			c: computedC * 100,
			m: computedM * 100,
			y: computedY * 100,
			k: computedK * 100
		};
	}

	#hsvToRGB(value) {
		let hsv = value.split(" ").slice(1).map(x => parseFloat(x)),
			h = Math.min(360, Math.min(0, hsv.at(0))), s = hsv.at(1), v = hsv.at(2);

		let f = (n, k = (n + h / 60) % 6) => v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);

		return {
			r: Math.round(f(5) * 255),
			g: Math.round(f(3) * 255),
			b: Math.round(f(1) * 255)
		};
	}

	#hslToRGB(value) {
		let hsl = value.split(" ").slice(1).map(x => parseFloat(x)),
			h = hsl.at(0), s = hsl.at(1), l = hsl.at(2);

		let r, g, b;

		h /= 360;

		function hue2rgb(p, q, t) {
			if (t < 0) t += 1;
			if (t > 1) t -= 1;
			if (t < 1 / 6) return p + (q - p) * 6 * t;
			if (t < 1 / 2) return q;
			if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
			return p;
		}
		if (s === 0) {
			r = g = b = l; // Achromatic
		} else {
			const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
			const p = 2 * l - q;

			r = hue2rgb(p, q, h + 1 / 3);
			g = hue2rgb(p, q, h);
			b = hue2rgb(p, q, h - 1 / 3);
		}
		return {
			r: Math.round(r * 255),
			g: Math.round(g * 255),
			b: Math.round(b * 255)
		};
	}

	#valueToRGB(value) {
		if (!value) { return { r: 0, g: 0, b: 0 } };
		if (value.startsWith("#")) { return this.#hexToRGB(value); }
		if (value.startsWith("hsl")) { return this.#hslToRGB(value); }
		if (value.startsWith("hsv")) { return this.#hsvToRGB(value); }
		if (value.startsWith("cmyk")) { return this.#cmykToRGB({ c: parseFloat(value.split(" ").at(1)), m: parseFloat(value.split(" ").at(2)), y: parseFloat(value.split(" ").at(3)), k: parseFloat(value.split(" ").at(3)) }); }

		const fixedValue = value.replace("rgb(", "").replace(")", "").replace(/ /g, "").split(",");

		return {
			r: parseInt(fixedValue.at(0)) || 0,
			g: parseInt(fixedValue.at(1)) || 0,
			b: parseInt(fixedValue.at(2)) || 0
		};
	}

	attributeChanged(name, _, newValue) {
		if (name === "value" && !this.#terminateValueSetter && !this.presets.length) {
			this.#colourToPos(newValue);
			this.#UpdateValue(newValue);
		}

		this.#terminateValueSetter = false;
	}
}
